import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, Button, Table, Alert } from 'reactstrap';
import { FileText, Upload, X, AlertCircle } from 'react-feather';
import API_BASE_URL from '../../../Config/api';
import authHeader from '../../Customer/authHeader';
import connectGoogleDrive from '../../Common/connectGoogleDrive';

const CreditNotesModal = ({ isOpen, toggle, customerId, customerName }) => {
    const [creditNotes, setCreditNotes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        credit_note_number: '',
        amount: '',
        currency: 'EUR',
        pdf: null
    });
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(true);
    const [authUrl, setAuthUrl] = useState('');

    useEffect(() => {
        if (isOpen && customerId) {
            fetchCreditNotes();
            checkAuthStatus();
        }
    }, [isOpen, customerId]);

    const checkAuthStatus = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/oauth/status`);
            const data = await response.json();
            setIsAuthenticated(data.authenticated);
        } catch (error) {
            console.error('Error checking auth status:', error);
        }
    };

    const fetchCreditNotes = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/customers/${customerId}/credit-notes`, authHeader());
            if (!response.ok) {
                throw new Error('Failed to fetch credit notes');
            }
            const data = await response.json();
            setCreditNotes(data);
        } catch (error) {
            console.error('Error fetching credit notes:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
        // Clear error for this field
        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: ''
            });
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type (only PDF)
            if (file.type !== 'application/pdf') {
                setErrors({
                    ...errors,
                    pdf: 'Only PDF files are allowed'
                });
                return;
            }
            setFormData({
                ...formData,
                pdf: file
            });
            // Clear error
            if (errors.pdf) {
                setErrors({
                    ...errors,
                    pdf: ''
                });
            }
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.credit_note_number || !formData.credit_note_number.trim()) {
            newErrors.credit_note_number = 'Credit Note Number is required';
        }

        if (!formData.amount || isNaN(formData.amount) || parseFloat(formData.amount) <= 0) {
            newErrors.amount = 'Valid amount is required';
        }

        if (!formData.pdf) {
            newErrors.pdf = 'PDF file is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleAuthenticate = async () => {
        const authWindow = await connectGoogleDrive('Google Drive Authentication', 'width=600,height=600');
        if (!authWindow) return;

        // Poll for authentication status
        const pollInterval = setInterval(async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/api/oauth/status`);
                const data = await response.json();
                if (data.authenticated) {
                    setIsAuthenticated(true);
                    clearInterval(pollInterval);
                    if (authWindow && !authWindow.closed) {
                        authWindow.close();
                    }
                    alert('Authentication successful! You can now upload credit notes.');
                }
            } catch (error) {
                console.error('Error checking auth status:', error);
            }
        }, 2000);

        // Stop polling after 5 minutes
        setTimeout(() => {
            clearInterval(pollInterval);
        }, 300000);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setSubmitting(true);

        try {
            console.log('[Credit Note Frontend] Submitting with currency:', formData.currency);
            const formDataToSend = new FormData();
            formDataToSend.append('credit_note_number', formData.credit_note_number);
            formDataToSend.append('amount', formData.amount);
            formDataToSend.append('currency', formData.currency);
            formDataToSend.append('pdf', formData.pdf);
            console.log('[Credit Note Frontend] FormData currency:', formDataToSend.get('currency'));

            const response = await fetch(`${API_BASE_URL}/api/customers/${customerId}/credit-notes`, {
                method: 'POST',
                ...authHeader(),
                body: formDataToSend
            });

            if (response.status === 401) {
                // Two different 401s reach here: the route needs a staff token,
                // and the upload itself needs Google Drive to be authorised. Only
                // the Drive one carries an authUrl, so don't send someone to the
                // Drive button when what actually expired is their session.
                const errorData = await response.json().catch(() => ({}));
                if (errorData.authUrl) {
                    setAuthUrl(errorData.authUrl);
                    setIsAuthenticated(false);
                    alert('Google Drive authentication required. Please click "Authenticate Google Drive" button.');
                } else {
                    alert('Your session has expired. Please sign in again.');
                }
                return;
            }

            if (response.ok) {
                alert('Credit note added successfully!');
                // Reset form
                setFormData({ credit_note_number: '', amount: '', currency: 'EUR', pdf: null });
                // Clear file input
                document.getElementById('pdf-input').value = '';
                // Refresh credit notes list
                fetchCreditNotes();
            } else {
                const errorData = await response.json();
                alert(`Failed to add credit note: ${errorData.error || 'Unknown error'}`);
            }
        } catch (error) {
            console.error('Error adding credit note:', error);
            alert('Error adding credit note. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const formatAmount = (amount) => {
        return parseFloat(amount).toFixed(2);
    };

    const getCurrencySymbol = (currency) => {
        const symbols = {
            'EUR': '€',
            'GBP': '£',
            'INR': '₹'
        };
        return symbols[currency] || currency;
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg">
            <ModalHeader toggle={toggle}>
                <FileText className="me-2" size={20} />
                Credit Notes - {customerName}
            </ModalHeader>
            <ModalBody>
                {/* Authentication Warning */}
                {!isAuthenticated && (
                    <Alert color="warning" className="mb-3">
                        <AlertCircle size={16} className="me-2" />
                        Google Drive authentication required to upload credit notes.
                        <Button
                            color="primary"
                            size="sm"
                            className="ms-2"
                            onClick={handleAuthenticate}
                        >
                            Authenticate Google Drive
                        </Button>
                    </Alert>
                )}

                {/* Add Credit Note Form */}
                <div className="mb-4 p-3 border rounded">
                    <h6 className="mb-3">Add New Credit Note</h6>
                    <Form onSubmit={handleSubmit}>
                        <div className="row">
                            <div className="col-md-6">
                                <FormGroup>
                                    <Label for="credit_note_number">Credit Note Number <span className="text-danger">*</span></Label>
                                    <Input
                                        type="text"
                                        name="credit_note_number"
                                        id="credit_note_number"
                                        value={formData.credit_note_number}
                                        onChange={handleChange}
                                        invalid={!!errors.credit_note_number}
                                        placeholder="Enter credit note number"
                                    />
                                    {errors.credit_note_number && <div className="text-danger small">{errors.credit_note_number}</div>}
                                </FormGroup>
                            </div>
                            <div className="col-md-6">
                                <FormGroup>
                                    <Label for="amount">Amount <span className="text-danger">*</span></Label>
                                    <div className="input-group">
                                        <Input
                                            type="select"
                                            name="currency"
                                            id="currency"
                                            value={formData.currency}
                                            onChange={handleChange}
                                            style={{ maxWidth: '120px' }}
                                        >
                                            <option value="EUR">EUR (€)</option>
                                            <option value="GBP">GBP (£)</option>
                                            <option value="INR">INR (₹)</option>
                                        </Input>
                                        <Input
                                            type="number"
                                            name="amount"
                                            id="amount"
                                            value={formData.amount}
                                            onChange={handleChange}
                                            invalid={!!errors.amount}
                                            placeholder="Enter amount"
                                            step="0.01"
                                            min="0"
                                        />
                                    </div>
                                    {errors.amount && <div className="text-danger small">{errors.amount}</div>}
                                </FormGroup>
                            </div>
                            <div className="col-md-12">
                                <FormGroup>
                                    <Label for="pdf-input">PDF File <span className="text-danger">*</span></Label>
                                    <Input
                                        type="file"
                                        id="pdf-input"
                                        accept=".pdf"
                                        onChange={handleFileChange}
                                        invalid={!!errors.pdf}
                                    />
                                    {errors.pdf && <div className="text-danger small">{errors.pdf}</div>}
                                    {formData.pdf && (
                                        <div className="text-success small mt-1">
                                            <Upload size={12} className="me-1" />
                                            {formData.pdf.name}
                                        </div>
                                    )}
                                </FormGroup>
                            </div>
                        </div>
                        <Button color="primary" type="submit" disabled={submitting} size="sm">
                            {submitting ? 'Adding...' : 'Add Credit Note'}
                        </Button>
                    </Form>
                </div>

                {/* Credit Notes List */}
                <div>
                    <h6 className="mb-3">Existing Credit Notes</h6>
                    {loading ? (
                        <div className="text-center p-4">
                            <div className="spinner-border spinner-border-sm" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : creditNotes.length === 0 ? (
                        <div className="text-center p-4 text-muted">
                            No credit notes found for this customer.
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <Table className="table-bordered table-sm">
                                <thead>
                                    <tr>
                                        <th>Credit Note #</th>
                                        <th>Date</th>
                                        <th>Amount</th>
                                        <th>PDF</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {creditNotes.map((note) => (
                                        <tr key={note.id}>
                                            <td>{note.credit_note_number || '-'}</td>
                                            <td>{formatDate(note.created_at)}</td>
                                            <td>{getCurrencySymbol(note.currency || 'EUR')}{formatAmount(note.amount)}</td>
                                            <td>
                                                <a
                                                    href={note.pdf_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="btn btn-sm btn-outline-primary"
                                                >
                                                    <FileText size={14} className="me-1" />
                                                    View PDF
                                                </a>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </div>
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={toggle}>
                    Close
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default CreditNotesModal;
